/**
 * Cliente HTTP estudios Gateway Edge: archive ZIP + ACK.
 * Multi-Edge: edge_wado_url → base API + Bearer JWT Controller.
 * Fallback legacy: sesión Edge local (udv-gateway-*).
 */
import JSZip from 'jszip'
import {
  loadSession,
  authHeaders,
  getOrCreateDeviceId,
} from './gatewayAuth'
import {
  loadControllerSession,
  authHeadersController,
} from './controllerAuth'
import { resolveEdgeApiBase } from '../utils/gatewayPushPayload'
import { putCachedInstance, limpiarCacheEstudio } from '../db/dicomCache'
import {
  upsertStudyCatalog,
  syncCatalogStatsFromCache,
  STUDY_SOURCE,
} from '../db/studyCatalog'
import { parseSopFromDicomBuffer } from '../utils/loadDicomInstanceWithCache'
import { parseDicomStudyMeta } from '../utils/parseDicomStudyMeta'

function gatewayUnavailableMessage(status) {
  if (status === 404 || status === 501) {
    return 'El Gateway aún no expone descarga/ACK de estudios (endpoints archive/ack pendientes en el Edge).'
  }
  if (status === 409) {
    return 'El estudio aún no terminó de ingerirse en el Gateway (ingest incompleto).'
  }
  return null
}

function parseMetaFromBuffer(buffer) {
  return parseDicomStudyMeta(buffer)
}

/**
 * Resuelve base API + headers Authorization para archive/ACK.
 * @param {{
 *   session?: object,
 *   edgeWadoUrl?: string,
 *   edgeApiBase?: string,
 *   edgeId?: string,
 *   bearerToken?: string,
 * }} opts
 * @returns {{ serverUrl: string, headers: object, edgeId?: string, edgeWadoUrl?: string, edgeApiBase?: string }}
 */
export function resolveStudyRequestTarget(opts) {
  const o = opts || {}
  const edgeWadoUrl = o.edgeWadoUrl || o.edge_wado_url
  const edgeApiBase =
    o.edgeApiBase ||
    (edgeWadoUrl ? resolveEdgeApiBase(edgeWadoUrl) : null)

  if (edgeApiBase) {
    const ctrl = loadControllerSession()
    const token =
      o.bearerToken ||
      (ctrl && ctrl.token) ||
      null
    if (!token) {
      throw new Error(
        'Se requiere JWT Controller para descargar desde el Edge de origen'
      )
    }
    return {
      serverUrl: edgeApiBase,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      edgeId: o.edgeId || o.edge_id || undefined,
      edgeWadoUrl: edgeWadoUrl || undefined,
      edgeApiBase,
    }
  }

  const session = o.session || loadSession()
  if (!session || !session.token) {
    throw new Error('No hay sesión de Gateway')
  }
  return {
    serverUrl: session.serverUrl,
    headers: { ...authHeaders(session) },
    session,
  }
}

/**
 * POST /api/v1/studies/{uid}/ack
 */
export async function ackStudy(studyInstanceUID, opts) {
  const o = opts || {}
  const target = resolveStudyRequestTarget(o)
  const uid = encodeURIComponent(String(studyInstanceUID || ''))
  const res = await fetch(`${target.serverUrl}/api/v1/studies/${uid}/ack`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...target.headers,
    },
    body: JSON.stringify({
      device_id: getOrCreateDeviceId(),
      decision: o.decision || 'accept',
      keep_local: !!o.keep_local,
      local_instances: o.local_instances != null ? Number(o.local_instances) : 0,
      replaced: !!o.replaced,
    }),
  })
  if (!res.ok) {
    const tip = gatewayUnavailableMessage(res.status)
    if (tip) {
      const err = new Error(tip)
      err.code = 'GATEWAY_ENDPOINT_MISSING'
      err.status = res.status
      throw err
    }
    const text = await res.text().catch(() => '')
    throw new Error(
      `ACK falló (HTTP ${res.status})${text ? `: ${text.slice(0, 160)}` : ''}`
    )
  }
  try {
    return await res.json()
  } catch (e) {
    return { ok: true }
  }
}

/**
 * Descarga ZIP, guarda SOPs en IDB y actualiza catálogo.
 */
export async function downloadAndStoreStudy(studyInstanceUID, opts) {
  const o = opts || {}
  const target = resolveStudyRequestTarget(o)
  const uid = String(studyInstanceUID || '')
  if (!uid) throw new Error('StudyInstanceUID vacío')

  const enc = encodeURIComponent(uid)
  const res = await fetch(`${target.serverUrl}/api/v1/studies/${enc}/archive`, {
    method: 'GET',
    headers: { ...target.headers },
  })

  if (!res.ok) {
    const tip = gatewayUnavailableMessage(res.status)
    if (tip) {
      const err = new Error(tip)
      err.code = 'GATEWAY_ENDPOINT_MISSING'
      err.status = res.status
      throw err
    }
    const text = await res.text().catch(() => '')
    throw new Error(
      `Descarga archive falló (HTTP ${res.status})${text ? `: ${text.slice(0, 160)}` : ''}`
    )
  }

  const blob = await res.blob()
  const zip = await JSZip.loadAsync(blob)
  const entries = Object.keys(zip.files).filter((name) => {
    const f = zip.files[name]
    return f && !f.dir && /\.(dcm|dicom)$/i.test(name)
  })
  const fileNames =
    entries.length > 0
      ? entries
      : Object.keys(zip.files).filter((name) => zip.files[name] && !zip.files[name].dir)

  if (fileNames.length === 0) {
    throw new Error('El archive ZIP no contiene instancias DICOM')
  }

  if (o.replace) {
    await limpiarCacheEstudio(uid)
  }

  let stored = 0
  let firstMeta = null
  for (let i = 0; i < fileNames.length; i++) {
    const name = fileNames[i]
    const buffer = await zip.files[name].async('arraybuffer')
    if (!buffer || buffer.byteLength < 128) continue
    let sop = parseSopFromDicomBuffer(buffer)
    if (!sop) sop = `zip:${uid}:${i}:${name}`
    const meta = parseMetaFromBuffer(buffer)
    if (!firstMeta && meta.studyInstanceUID) firstMeta = meta
    await putCachedInstance({
      sopInstanceUID: sop,
      studyInstanceUID: uid,
      blob: buffer,
      status: 'complete',
      contentType: 'application/dicom',
    })
    stored += 1
    if (typeof o.onProgress === 'function') {
      o.onProgress({ done: i + 1, total: fileNames.length })
    }
  }

  if (stored === 0) {
    throw new Error('No se pudo guardar ninguna instancia del archive')
  }

  const catalogMeta = {
    studyInstanceUID: uid,
    patientName:
      o.patientName ||
      (firstMeta && firstMeta.patientName) ||
      'Desconocido',
    patientId: (firstMeta && firstMeta.patientId) || '',
    studyDate: (firstMeta && firstMeta.studyDate) || '',
    modality:
      o.modality ||
      (firstMeta && firstMeta.modality) ||
      '',
    description: (firstMeta && firstMeta.description) || '',
    source: STUDY_SOURCE.GATEWAY,
    gatewayReceivedInstances:
      o.receivedInstances != null ? Number(o.receivedInstances) : null,
    ingestComplete: o.ingestComplete != null ? !!o.ingestComplete : true,
    edgeId: target.edgeId || o.edgeId || null,
    edgeApiBase: target.edgeApiBase || target.serverUrl || null,
    edgeWadoUrl: target.edgeWadoUrl || o.edgeWadoUrl || null,
    clinicName: o.clinicName || o.clinic_name || null,
  }
  await upsertStudyCatalog(catalogMeta)
  const synced = await syncCatalogStatsFromCache(uid, catalogMeta)

  return {
    studyInstanceUID: uid,
    stored,
    catalog: synced,
    edgeId: catalogMeta.edgeId,
    edgeApiBase: catalogMeta.edgeApiBase,
  }
}

/**
 * Flujo completo: download (opcional) + ack.
 */
export async function acceptStudyFromGateway(studyInstanceUID, opts) {
  const o = opts || {}
  const result = await downloadAndStoreStudy(studyInstanceUID, o)
  try {
    await ackStudy(studyInstanceUID, {
      decision: 'accept',
      keep_local: false,
      local_instances: result.stored,
      replaced: !!o.replace,
      session: o.session,
      edgeWadoUrl: o.edgeWadoUrl || o.edge_wado_url,
      edgeApiBase: o.edgeApiBase || (result && result.edgeApiBase),
      edgeId: o.edgeId || o.edge_id,
      bearerToken: o.bearerToken,
    })
  } catch (e) {
    if (e && e.code === 'GATEWAY_ENDPOINT_MISSING') {
      result.ackSkipped = true
      result.ackError = e.message
      return result
    }
    result.ackError = e && e.message ? e.message : String(e)
  }
  return result
}

export async function rejectStudyOnGateway(studyInstanceUID, opts) {
  const o = opts || {}
  return ackStudy(studyInstanceUID, {
    decision: 'reject',
    keep_local: false,
    local_instances: o.local_instances || 0,
    replaced: false,
    session: o.session,
    edgeWadoUrl: o.edgeWadoUrl,
    edgeApiBase: o.edgeApiBase,
    edgeId: o.edgeId,
    bearerToken: o.bearerToken,
  })
}

export async function keepLocalStudyAck(studyInstanceUID, opts) {
  const o = opts || {}
  return ackStudy(studyInstanceUID, {
    decision: 'accept',
    keep_local: true,
    local_instances: o.local_instances || 0,
    replaced: false,
    session: o.session,
    edgeWadoUrl: o.edgeWadoUrl,
    edgeApiBase: o.edgeApiBase,
    edgeId: o.edgeId,
    bearerToken: o.bearerToken,
  })
}
